import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import LoginPage from './components/LoginPage';
import PasswordRecovery from './components/management/PasswordRecovery';
import AdminDashboard from './components/AdminDashboard';
import TeacherDashboard from './components/TeacherDashboard';
import ParentDashboard from './components/ParentDashboard';
import SuperAdminDashboard from './components/SuperAdminDashboard';
import SetupPage from './components/SetupPage';
import ForcePasswordChange from './components/ForcePasswordChange';
import ImpersonationBanner from './components/ImpersonationBanner';
import { Toaster } from './components/ui/sonner';
import './App.css';

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
const API = `${BACKEND_URL}/api`;

// Configure axios defaults
axios.defaults.baseURL = API;
// Note: withCredentials is NOT set globally to avoid CORS issues when frontend
// and backend are on different domains (e.g. custom domain + preview URL).
// The httpOnly cookie works for same-origin requests automatically.
// Bearer token (stored in React state) handles cross-origin authenticated requests.

// Multi-tenancy is handled transparently by the backend:
// - Super Admin: found because their user has `school_id: null`.
// - School users: backend resolves the school automatically from the user record.
// The `?school=CODE` query parameter is ONLY honored for internal testing
// (e.g., during development with a shared DB); it is never shown to end users.
function detectSchoolCode() {
  const params = new URLSearchParams(window.location.search);
  const querySchool = params.get('school');
  return querySchool ? querySchool.toUpperCase() : null;
}

const detectedSchoolCode = detectSchoolCode();

// Auth context
const AuthContext = React.createContext();

export const useAuth = () => {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);   // In-memory only — NOT in localStorage (XSS protection)
  const [loading, setLoading] = useState(true);
  const [schoolInfo, setSchoolInfo] = useState(null);

  useEffect(() => {
    // On app load, try to restore session via the httpOnly cookie set by login.
    // This call uses withCredentials so the cookie is sent cross-origin if needed.
    // If valid → we get a fresh token + user info. If not → clear stale localStorage.
    const restoreSession = async () => {
      if (!localStorage.getItem('user')) {
        setLoading(false);
        return;
      }

      try {
        const res = await axios.get('/auth/me', { withCredentials: true });
        const d = res.data;
        const userData = {
          username: d.username,
          role: d.role,
          user_id: d.user_id,
          school_id: d.school_id,
          is_primary_parent: d.is_primary_parent,
          admin_subrole: d.admin_subrole,
          must_change_password: !!d.must_change_password,
          impersonated_by: d.impersonated_by || null,
        };
        const school = d.school_id ? {
          school_id: d.school_id,
          school_name: d.school_name,
          school_logo_url: d.school_logo_url,
          school_primary_color: d.school_primary_color,
          school_secondary_color: d.school_secondary_color,
          school_accent_color: d.school_accent_color,
        } : null;
        setToken(d.access_token);
        setUser(userData);
        setSchoolInfo(school);
        axios.defaults.headers.common['Authorization'] = `Bearer ${d.access_token}`;
        localStorage.setItem('user', JSON.stringify(userData));
        if (school) localStorage.setItem('schoolInfo', JSON.stringify(school));
      } catch (_) {
        // Cookie missing/expired/blocked → clear stale state, force login
        localStorage.removeItem('user');
        localStorage.removeItem('schoolInfo');
        setUser(null);
        setSchoolInfo(null);
        setToken(null);
      } finally {
        setLoading(false);
      }
    };
    restoreSession();
  }, []);

  const login = async (username, password, totp_code = '') => {
    try {
      const payload = { username, password, ...(totp_code ? {totp_code} : {}) };
      // Auto-attach school code from subdomain
      if (detectedSchoolCode) payload.school_code = detectedSchoolCode;
      
      const response = await axios.post('/auth/login', payload);
      const { access_token, role, user_id, school_id, school_name, school_logo_url, school_primary_color, school_secondary_color, school_accent_color, is_primary_parent } = response.data;
      
      const userData = { username, role, user_id, school_id, is_primary_parent, admin_subrole: response.data.admin_subrole, must_change_password: !!response.data.must_change_password };
      const school = school_id ? {
        school_id,
        school_name,
        school_logo_url,
        school_primary_color,
        school_secondary_color,
        school_accent_color,
      } : null;
      
      setToken(access_token);
      setUser(userData);
      setSchoolInfo(school);
      // Token is kept in React memory only — NOT in localStorage (XSS protection)
      // The server also sets an httpOnly cookie for page-refresh auth
      // Set Authorization header from in-memory token for this session
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      localStorage.setItem('user', JSON.stringify(userData));
      if (school) {
        localStorage.setItem('schoolInfo', JSON.stringify(school));
      } else {
        localStorage.removeItem('schoolInfo');
      }
      // Clean up any token that may have been left by old sessions
      localStorage.removeItem('token');
      
      return { success: true, role };
    } catch (error) {
      console.error('Login error:', error);
      return { 
        success: false, 
        error: error.response?.data?.detail?.message || error.response?.data?.detail || 'No se pudo iniciar sesión',
        needsTotp: error.response?.data?.detail?.code === 'totp_required'
      };
    }
  };

  const logout = async () => {
    try {
      // Clear httpOnly cookie server-side (withCredentials needed only for this call)
      await axios.post('/auth/logout', {}, { withCredentials: true });
    } catch (_) { /* ignore errors on logout */ }
    setToken(null);
    setUser(null);
    setSchoolInfo(null);
    localStorage.removeItem('user');
    localStorage.removeItem('schoolInfo');
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
  };

  const clearMustChangePassword = () => {
    setUser(prev => {
      if (!prev) return prev;
      const next = { ...prev, must_change_password: false };
      localStorage.setItem('user', JSON.stringify(next));
      return next;
    });
  };

  const value = {
    user,
    token,
    login,
    logout,
    loading,
    schoolInfo,
    detectedSchoolCode,
    clearMustChangePassword,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/unauthorized" replace />;
  }

  return children;
};

const DashboardRouter = () => {
  const { user, schoolInfo } = useAuth();

  let dashboard;
  if (user?.role === 'super_admin') dashboard = <SuperAdminDashboard />;
  else if (user?.role === 'admin') dashboard = <AdminDashboard />;
  else if (user?.role === 'teacher') dashboard = <TeacherDashboard />;
  else if (user?.role === 'parent') dashboard = <ParentDashboard />;
  else return <Navigate to="/login" replace />;

  return (
    <>
      <ImpersonationBanner user={user} schoolInfo={schoolInfo} />
      {dashboard}
    </>
  );
};

function App() {
  return (
    <div className="App min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/reset-password" element={<PasswordRecovery />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/setup" element={<SetupPage />} />
            <Route 
              path="/dashboard" 
              element={
                <ProtectedRoute>
                  <DashboardRouter />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin" 
              element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <AdminDashboard />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/teacher" 
              element={
                <ProtectedRoute allowedRoles={['teacher', 'admin']}>
                  <TeacherDashboard />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/parent" 
              element={
                <ProtectedRoute allowedRoles={['parent']}>
                  <ParentDashboard />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/super-admin" 
              element={
                <ProtectedRoute allowedRoles={['super_admin']}>
                  <SuperAdminDashboard />
                </ProtectedRoute>
              } 
            />
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route 
              path="/unauthorized" 
              element={
                <div className="min-h-screen flex items-center justify-center">
                  <div className="text-center">
                    <h1 className="text-2xl font-bold text-red-600 mb-4">Acceso No Autorizado</h1>
                    <p className="text-gray-600">No tienes permisos para acceder a esta página.</p>
                  </div>
                </div>
              } 
            />
          </Routes>
          <ForcePasswordGate />
        </BrowserRouter>
        <Toaster />
      </AuthProvider>
    </div>
  );
}

const ForcePasswordGate = () => {
  const { user } = useAuth();
  if (user && user.must_change_password) return <ForcePasswordChange />;
  return null;
};

export default App;
